import { useEffect, useMemo, useState } from 'react';

import { Calendar } from '@/components/ui/Calendar/Calendar';
import { useTimeFor } from '@/hooks/useTimeFor';
import { cn } from '@/utils';
import { centerDayFromKey, toCenterDayKey } from '@/utils/timezone';
import { endOfMonth, isValid, startOfMonth } from 'date-fns';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CALENDAR_DEFAULT_NAV_BUTTON_CLASSNAMES } from '../SchedulingCalendar/SchedulingCalendar';

interface RecurringCalendarProps extends Omit<React.ComponentProps<typeof Calendar>, 'value' | 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  maxRecurringDate: Date;
  firstSessionDate: Date;
}

export const RecurringCalendar = ({ value, firstSessionDate, onChange, maxRecurringDate, ...props }: RecurringCalendarProps) => {
  const { t } = useTranslation('scheduling', { keyPrefix: 'recurringCalendar' });

  const { timeZone: centerTimeZone, now } = useTimeFor('sessionTime');

  const [displayMonth, setDisplayMonth] = useState<Date>(() => (value ? centerDayFromKey(value, centerTimeZone) : now()));

  const selectedDate = useMemo(() => (value ? centerDayFromKey(value, centerTimeZone) : undefined), [value, centerTimeZone]);

  const firstSessionTime = firstSessionDate?.getTime();

  useEffect(() => {
    if (firstSessionDate && isValid(firstSessionDate)) {
      setDisplayMonth(firstSessionDate);
      onChange?.('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSessionTime]);

  const handleDateSelect = (newDate: Date | undefined) => {
    if (!newDate) return;

    onChange?.(toCenterDayKey(newDate, centerTimeZone));
  };

  const isCustomCalendarDateDisabled = (date: Date) => {
    if (date < firstSessionDate) return true;
    if (maxRecurringDate && date > maxRecurringDate) return true;
    return false;
  };

  const shouldShowAdvanceWarning = (() => {
    const firstDayOfMonth = startOfMonth(displayMonth);
    const lastDayOfMonth = endOfMonth(displayMonth);

    if (firstDayOfMonth < firstSessionDate) return false;

    return isCustomCalendarDateDisabled(firstDayOfMonth) && isCustomCalendarDateDisabled(lastDayOfMonth);
  })();

  const shouldHidePreviousMonthButton = displayMonth
    ? displayMonth.getMonth() <= firstSessionDate.getMonth() && displayMonth.getFullYear() <= firstSessionDate.getFullYear()
    : false;

  if (!firstSessionDate || !isValid(firstSessionDate)) {
    return <p className='text-text-default-secondary'>{t('pleaseSelectDate')}</p>;
  }

  return (
    <Calendar
      {...props}
      mode='single'
      timeZone={centerTimeZone}
      className='mt-4'
      month={displayMonth}
      onMonthChange={setDisplayMonth}
      selected={selectedDate}
      onSelect={handleDateSelect}
      disabled={isCustomCalendarDateDisabled}
      showOutsideDays={false}
      classNames={{
        button_previous: cn(CALENDAR_DEFAULT_NAV_BUTTON_CLASSNAMES, {
          invisible: shouldHidePreviousMonthButton,
        }),
        button_next: cn(CALENDAR_DEFAULT_NAV_BUTTON_CLASSNAMES, {
          invisible: shouldShowAdvanceWarning,
        }),
      }}
      contentBelowNavigation={
        shouldShowAdvanceWarning ? (
          <div className='bg-fill-background-light flex items-center gap-4 rounded-lg px-5 py-4'>
            <Info className='text-text-default-secondary size-6 shrink-0' />
            <p className='text-body-m-regular'>{t('advanceWarning.description')}</p>
          </div>
        ) : undefined
      }
    />
  );
};
